import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead } from "@/components/shared";

const TURNOVER_COST = { 1: 70, 2: 90, 3: 115, 4: 140, 5: 165 };
const PLATFORM_FEE = { Airbnb: 0.03, VRBO: 0.08, Direct: 0 };
const MGMT_FEE = { "Self-managed": 0, "Co-host (15%)": 0.15, "Full-service (25%)": 0.25, "White-glove (50%)": 0.5 };

const Row = ({ label, value, strong, accent, testid }) => (
  <div className={`flex items-baseline justify-between gap-4 py-3 ${strong ? "border-t border-white/15 pt-4" : ""}`}>
    <span className={`text-sm ${strong ? "font-medium text-ivory" : "text-ivory/60"}`}>{label}</span>
    <span data-testid={testid} className={`font-mono ${strong ? "text-2xl text-bermuda" : "text-base"} ${accent ? "text-bermuda" : strong ? "" : "text-ivory/85"}`}>{value}</span>
  </div>
);

export default function ProfitCalculator() {
  const [bedrooms, setBedrooms] = useState(2);
  const [rate, setRate] = useState(350);
  const [occupancy, setOccupancy] = useState(65);
  const [stay, setStay] = useState(5);
  const [cleaningFee, setCleaningFee] = useState(120);
  const [platform, setPlatform] = useState("Airbnb");
  const [management, setManagement] = useState("Co-host (15%)");

  const r = useMemo(() => {
    const nights = 30.4 * (occupancy / 100);
    const bookings = Math.max(1, Math.round(nights / stay));
    const gross = Math.round(nights * rate);
    const platformCost = Math.round(gross * PLATFORM_FEE[platform]);
    const mgmtCost = Math.round(gross * MGMT_FEE[management]);
    const rentalFee = Math.round(gross * 0.045);
    const rentalFeeNote = platform === "Airbnb" ? "Collected & remitted by Airbnb" : platform === "VRBO" ? "Collected by Vrbo for qualifying bookings" : "Owner remits";
    const turnovers = bookings;
    const cleaningCost = turnovers * TURNOVER_COST[bedrooms];
    const cleaningCharged = turnovers * cleaningFee;
    const net = gross - platformCost - mgmtCost - rentalFee - cleaningCost + cleaningCharged;
    return { nights: Math.round(nights), bookings, gross, platformCost, mgmtCost, rentalFee, rentalFeeNote, turnovers, cleaningCost, cleaningCharged, net };
  }, [bedrooms, rate, occupancy, stay, cleaningFee, platform, management]);

  const inputCls = "w-full appearance-none border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none focus:border-bermuda-dark";

  return (
    <section id="calculator" className="scroll-mt-24 bg-white" data-testid="profit-calculator">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <SectionHead
          overline="The Owner's Calculator"
          title={<>The Bermuda Vacation Rental<br /><span className="italic text-bermuda-dark">Profit Calculator.</span></>}
          copy="Real numbers, not vibes. Adjust the sliders to see bookings, turnover requirements, expenses and estimated owner revenue — including Bermuda's 4.5% Vacation Rental Fee."
        />

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="space-y-8">
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="calc-bedrooms" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Bedrooms</label>
                  <span className="font-mono text-sm text-navy">{bedrooms}{bedrooms === 5 ? "+" : ""}</span>
                </div>
                <input id="calc-bedrooms" data-testid="calc-bedrooms" type="range" min="1" max="5" step="1" value={bedrooms} onChange={(e) => setBedrooms(+e.target.value)} className="mt-3 w-full accent-[#C48B7F]" />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="calc-rate" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Average Nightly Rate</label>
                  <span className="font-mono text-sm text-navy">${rate}</span>
                </div>
                <input id="calc-rate" data-testid="calc-rate" type="range" min="150" max="800" step="10" value={rate} onChange={(e) => setRate(+e.target.value)} className="mt-3 w-full accent-[#C48B7F]" />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="calc-occupancy" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Estimated Occupancy</label>
                  <span className="font-mono text-sm text-navy">{occupancy}%</span>
                </div>
                <input id="calc-occupancy" data-testid="calc-occupancy" type="range" min="30" max="90" step="5" value={occupancy} onChange={(e) => setOccupancy(+e.target.value)} className="mt-3 w-full accent-[#C48B7F]" />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <label htmlFor="calc-cleaning" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Guest Cleaning Fee</label>
                  <span className="font-mono text-sm text-navy">${cleaningFee}</span>
                </div>
                <input id="calc-cleaning" data-testid="calc-cleaning" type="range" min="60" max="250" step="5" value={cleaningFee} onChange={(e) => setCleaningFee(+e.target.value)} className="mt-3 w-full accent-[#C48B7F]" />
              </div>
              <div className="grid gap-5 sm:grid-cols-3">
                <label className="block">
                  <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Average Stay</span>
                  <select data-testid="calc-stay" value={stay} onChange={(e) => setStay(+e.target.value)} className={inputCls}>
                    {[3, 5, 7].map((s) => <option key={s} value={s}>{s} nights</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Platform</span>
                  <select data-testid="calc-platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className={inputCls}>
                    {Object.keys(PLATFORM_FEE).map((p) => <option key={p}>{p}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Management</span>
                  <select data-testid="calc-management" value={management} onChange={(e) => setManagement(e.target.value)} className={inputCls}>
                    {Object.keys(MGMT_FEE).map((m) => <option key={m}>{m}</option>)}
                  </select>
                </label>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="flex">
            <div className="noise-overlay flex w-full flex-col justify-center bg-navy p-9 text-ivory lg:p-12" data-testid="calc-results">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">Estimated Monthly Picture</p>
              <div className="mt-6 divide-y divide-white/10">
                <Row label="Booking revenue" value={`$${r.gross.toLocaleString()}`} testid="calc-gross" />
                <Row label={`Bookings / month (${stay}-night avg stays)`} value={`≈ ${r.bookings}`} testid="calc-bookings" />
                <Row label="Cleaning turnovers required" value={r.turnovers} testid="calc-turnovers" />
                <Row label={`Platform fees (${platform})`} value={`−$${r.platformCost.toLocaleString()}`} />
                <Row label={`Management (${management.split(" (")[0].toLowerCase()})`} value={r.mgmtCost ? `−$${r.mgmtCost.toLocaleString()}` : "—"} />
                <Row label="Cleaning costs (net of guest fees)" value={r.cleaningCost - r.cleaningCharged > 0 ? `−$${(r.cleaningCost - r.cleaningCharged).toLocaleString()}` : "Covered by guest fees"} testid="calc-cleaning-net" />
                <Row label="Bermuda Vacation Rental Fee (4.5%)" value={`−$${r.rentalFee.toLocaleString()}`} testid="calc-rental-fee" />
              </div>
              <p className="mt-1 text-[11px] text-ivory/45">{r.rentalFeeNote} · Tourism levy on registered vacation rentals</p>
              <div className="mt-4">
                <Row strong label="Estimated owner revenue" value={`$${r.net.toLocaleString()}/mo`} testid="calc-net" />
                <p className="text-right font-mono text-sm text-ivory/55">≈ ${(r.net * 12).toLocaleString()} / year</p>
              </div>
              <p className="mt-6 text-[11px] leading-relaxed text-ivory/40">
                Figures are estimates for illustration, not guaranteed earnings. Seasonality, events and your property's appeal move real results.
              </p>
              <Link to="/assessment" data-testid="calc-assessment-btn" className="group mt-7 inline-flex w-fit items-center gap-2.5 bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory">
                Get My Free Property Assessment
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <p className="mt-3 text-[11px] text-ivory/45">Want us to calculate this using your actual property?</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
